"use client";

import { useRouter } from "next/navigation";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/store";
import { tokenStorage } from "@/lib/utils/tokenStorage";
import { setAuth, clearAuth, UserRole } from "@/store/slices/authSlice";
import { logout as logoutService } from "@/lib/api/services/auth.service";
import { User } from "@/types/auth-types";
import { useEffect, useState } from "react";
import {
  isAdminUser,
  redirectStoredAdminToPortal,
} from "@/lib/utils/admin-portal";

export const useAuth = () => {
  const router = useRouter();
  const dispatch = useDispatch();
  const { user, isAuthenticated } = useSelector(
    (state: RootState) => state.auth,
  );
  const [isHydrated, setIsHydrated] = useState(false);

  // ✅ Rehydrate Redux from localStorage on every mount
  // Covers page refresh where Redux resets but tokens survive
  useEffect(() => {
    const token = tokenStorage.getAccessToken();
    const storedUser = tokenStorage.getUser() as User | null;

    if (token && storedUser && !isAuthenticated) {
      dispatch(setAuth(storedUser)); // ✅ restores user + role into Redux
    }

    setIsHydrated(true);
  }, [dispatch, isAuthenticated]); // ✅ rehydrate when auth state is still empty

  // ── Role helpers ──────────────────────────────────────────────────────────
  const role = (user?.role ??
    tokenStorage.getUser()?.role ??
    null) as UserRole | null;
  const isUser = role === "USER";

  // ── Guards ────────────────────────────────────────────────────────────────

  // Redirect to login if no token
  const requireAuth = () => {
    const token = tokenStorage.getAccessToken();
    if (!token) router.push("/login");
  };

  // Redirect to dashboard if already logged in (for login/register pages)
  const redirectIfAuthenticated = () => {
    const token = tokenStorage.getAccessToken();
    const storedUser = tokenStorage.getUser() as User | null;
    const resolvedUser = user ?? storedUser;

    if (!token) return;

    if (resolvedUser && isAdminUser(resolvedUser)) {
      redirectStoredAdminToPortal(resolvedUser);
      return;
    }

    router.push("/dashboard");
  };

  // ✅ Redirect if not USER role
  const requireUser = () => {
    const token = tokenStorage.getAccessToken();
    const storedUser = tokenStorage.getUser() as User | null;
    const userRole = user?.role ?? storedUser?.role;

    if (!token) {
      router.push("/login");
      return;
    }
    if (userRole !== "USER") {
      router.push("/unauthorized");
    }
  };

  // ── Auth actions ──────────────────────────────────────────────────────────

  const loginUser = (
    userData: User,
    tokens: { access: string; refresh: string; jti: string },
  ) => {
    tokenStorage.setTokens(tokens.access, tokens.refresh, tokens.jti);
    tokenStorage.setUser(userData); // ✅ persist user for rehydration
    dispatch(setAuth(userData));
  };

  const logoutUser = async () => {
    try {
      await logoutService();
    } finally {
      tokenStorage.clearTokens(); // ✅ clears user too
      dispatch(clearAuth());
      router.push("/login");
    }
  };

  return {
    user,
    isAuthenticated: isAuthenticated || !!tokenStorage.getAccessToken(),
    isHydrated,
    role,
    isUser, // ✅ boolean shorthand
    loginUser,
    logoutUser,
    requireAuth,
    requireUser, // ✅ new
    redirectIfAuthenticated,
  };
};

// How to use

// // ── Protect any page ──────────────────────────────────────────────────
// // Generic auth (any logged-in user)
// const { requireAuth, isHydrated } = useAuth();
// useEffect(() => { requireAuth(); }, []);

// // User-only page
// const { requireUser, isHydrated } = useAuth();
// useEffect(() => { requireUser(); }, []);

// // ── Conditional UI ────────────────────────────────────────────────────
// const { isUser, user, isHydrated } = useAuth();

// if (!isHydrated) return <Loader />; // ✅ wait for hydration before rendering

// return (
//   <div>
//     <p>Welcome {user?.first_name}</p>
//     {isUser && <UserDashboard />} // ✅ only renders for USER
//   </div>
// );

// // ── After login ───────────────────────────────────────────────────────
// const { loginUser } = useAuth();

// const handleLogin = async () => {
//   const res = await login({ email, password });
//   loginUser(res.data.data.user, {
//     access: res.data.data.access_token,
//     refresh: res.data.data.refresh_token,
//     jti: res.data.data.refresh_jti,
//   });
//   // role is now in Redux + localStorage — isUser works immediately
// };
